'use client';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { type BannerImageLoading, SandboxedHtml } from './_components/SandboxedHtml';

import { LoginModal } from '@/app/_modals/LoginModal';
import { RegisterModal } from '@/app/_modals/RegisterModal';
import { useModal } from '@/app/_providers/ModalProvider';
import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';
import { ContactType } from '@/shared/constants';
import { ModalProps, UrlModalData } from '@/shared/types';
import { getContacts, isAnalyticsGoal, reachGoal } from '@/shared/utils';

export const BANNER_ACTION_ATTR = 'data-banner-action';
export const BANNER_GOAL_ATTR = 'data-analytics-goal';

const JIVO_ACTION = 'jivo';

const MODAL_ACTIONS: Record<string, React.ComponentType<ModalProps<UrlModalData>>> = {
    register: RegisterModal,
    login: LoginModal,
};

const CONTACT_ACTIONS = new Set<string>(Object.values(ContactType));

type ContactMap = ReturnType<typeof getContacts>;

// The banner lives in a shadow root, so `event.target` is the host: walk the composed path up to it instead.
function findInBanner(event: React.MouseEvent<HTMLDivElement>, attribute: string): HTMLElement | undefined {
    const path = event.nativeEvent.composedPath();

    return path
        .slice(0, path.indexOf(event.currentTarget))
        .find((node): node is HTMLElement => node instanceof HTMLElement && node.hasAttribute(attribute));
}

function resolveContactHref(action: string, contacts: ContactMap): string | null {
    const value = contacts[action as ContactType];

    if (!value) {
        return null;
    }

    if (action === ContactType.PHONE) {
        return `tel:${value}`;
    }

    if (action === ContactType.EMAIL) {
        return `mailto:${value}`;
    }

    return value;
}

interface BannerHtmlProps {
    html: string;
    /** How the banner's images load. @default 'lazy' */
    imageLoading?: BannerImageLoading;
    className?: string;
}

/** A CMS banner (HTML) rendered in a sandbox; its links may open the sign-in / registration modals and report analytics goals. */
export function BannerHtml({ html, imageLoading, className }: BannerHtmlProps): React.ReactElement {
    const { contacts, region } = useSiteConfig();
    const { openModal } = useModal();
    const rootRef = useRef<HTMLDivElement>(null);

    const contactMap = useMemo(() => getContacts(contacts), [contacts]);

    useEffect(() => {
        const root = rootRef.current?.shadowRoot;

        if (!root) {
            return;
        }

        root.querySelectorAll<HTMLElement>(`[${BANNER_ACTION_ATTR}]`).forEach((element) => {
            const action = element.getAttribute(BANNER_ACTION_ATTR);

            if (!action || !CONTACT_ACTIONS.has(action) || !(element instanceof HTMLAnchorElement)) {
                return;
            }

            const href = resolveContactHref(action, contactMap);

            if (!href) {
                element.removeAttribute('href');
                return;
            }

            element.setAttribute('href', href);

            if (action !== ContactType.PHONE && action !== ContactType.EMAIL) {
                element.setAttribute('target', '_blank');
                element.setAttribute('rel', 'noopener noreferrer');
            }
        });
    }, [html, contactMap]);

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>): void => {
            const goal = findInBanner(event, BANNER_GOAL_ATTR)?.getAttribute(BANNER_GOAL_ATTR);

            if (goal && isAnalyticsGoal(goal)) {
                reachGoal(region.code, goal);
            }

            const target = findInBanner(event, BANNER_ACTION_ATTR);
            const action = target?.getAttribute(BANNER_ACTION_ATTR);

            if (!action) {
                return;
            }

            if (action === JIVO_ACTION) {
                event.preventDefault();
                window.jivo_api?.open();
                return;
            }

            const ModalComponent = MODAL_ACTIONS[action];

            if (ModalComponent) {
                event.preventDefault();
                openModal({ ModalComponent });
                return;
            }

            if (!CONTACT_ACTIONS.has(action)) {
                return;
            }

            const href = resolveContactHref(action, contactMap);

            if (!href) {
                event.preventDefault();
                return;
            }

            if (!(target instanceof HTMLAnchorElement)) {
                event.preventDefault();
                window.open(href, '_blank', 'noopener,noreferrer');
            }
        },
        [contactMap, openModal, region.code],
    );

    return (
        <SandboxedHtml
            ref={rootRef}
            html={html}
            imageLoading={imageLoading}
            className={className}
            onClick={handleClick}
        />
    );
}
